import { Button, Form, Input } from 'antd';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import useApi from '../../../utils/api';
import { login, sendCodeToRecoverPassword } from '../../../utils/actions';
import { notificationMessage } from '../../../utils/hooks';
import { useAnimalHeaven } from '../../../context/animalHeaven.provider';
import { setToken } from '../../../context/animalHeaven.actions';

const LoginForm = ({
  onClose,
  setEmail,
  setView,
}: {
  onClose: Function;
  setEmail: Function;
  setView: Function;
}) => {
  const [isSendingCode, setIsSendingCode] = useState<boolean>(false);
  const [isLogging, setIsLogging] = useState<boolean>(false);
  const [hasFormErrors, setHasFormErrors] = useState<boolean>(true);
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { animalHeavenDispatch } = useAnimalHeaven();
  const { api } = useApi();

  const onFinish = () => {
    const { email, password } = form.getFieldsValue();
    setIsLogging(true);
    api(login({ email, password }))
      .then((resp) => {
        if (resp.status === 200) {
          animalHeavenDispatch(setToken(resp.data.token));
          onClose();
          notificationMessage({
            message: t('login.successfullyLogin'),
            type: 'success',
          });
        }
      })
      .finally(() => setIsLogging(false));
  };
  const checkValidForm = () => {
    const { email, password } = form.getFieldsValue();
    const hasInputWithErrors =
      !email ||
      !password ||
      form
        .getFieldsError()
        .map((e) => e.errors)
        .flat().length !== 0;

    setHasFormErrors(hasInputWithErrors);
  };

  const onRecoveryPassword = () => {
    form.validateFields(['email']);
    let regex = /[a-z0-9]+@[a-z]+\.[a-z]{2,3}/;
    const { email } = form.getFieldsValue();

    if (email && regex.test(email)) {
      setIsSendingCode(true);
      api(sendCodeToRecoverPassword({ email }))
        .then((resp) => {
          if (resp.status === 200) {
            notificationMessage({
              message: t('login.weHaveSentCode'),
              type: 'success',
            });
            setView('recoveryPassword');
            setEmail(email);
          }
        })
        .finally(() => setIsSendingCode(false));
    }
  };

  return (
    <div className="h-full md:px-[52px]">
      <h1 className="mb-[32px] mt-[72px] hidden text-center md:block">{t('login.start')}</h1>
      <h3 className="mb-[32px] mt-[24px] text-center md:hidden">{t('login.start')}</h3>
      <Form form={form} onFinish={onFinish} onFieldsChange={checkValidForm} scrollToFirstError>
        <Form.Item
          label={t('common.email.label')}
          name="email"
          required
          rules={[
            {
              required: true,
              message: String(t('common.email.required')),
            },
            {
              type: 'email',
              message: String(t('common.email.error')),
            },
          ]}
        >
          <Input placeholder={String(t('common.email.placeHolder'))} />
        </Form.Item>
        <Form.Item
          label={t('common.password.label')}
          name="password"
          rules={[
            {
              required: true,
              message: String(t('common.password.required')),
            },
          ]}
        >
          <Input.Password placeholder={String(t('login.enterYourPassword'))} />
        </Form.Item>
        <Button
          onClick={onRecoveryPassword}
          htmlType="button"
          className="ghost flex px-[8px]"
          loading={isSendingCode}
        >
          <p className="link">{t('login.forgotPassword')}</p>
        </Button>
        <Button
          loading={isLogging}
          className={`primary mt-[40px] w-full ${hasFormErrors ? 'primaryDisabled' : ''}`}
          htmlType="submit"
        >
          {t('appBar.login')}
        </Button>
        <div className="mb-[16px] mt-[48px] text-center md:mb-[40px] md:mt-[112px]">
          <Button
            onClick={() => {
              navigate('/add-memorial');
              onClose();
            }}
            htmlType="button"
            className="ghost"
          >
            <p className="link">{t('login.orRegisterHere')}</p>
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default LoginForm;
